import { createMachine } from 'xstate';
import { enrollmentContext } from '../enrollment-selection';
import { uploadContext } from '../upload';
import { userContext } from '../user-creation';
import { utilityContext } from '../utility-selection';
import { perchMachineActions } from './defs/actions';
import {
  enrollmentContextOverrides,
  uploadContextOverrides,
  userContextOverrides,
  utilityContextOverrides,
} from './defs/context-overrides';
import { perchMachineGuards } from './defs/guards';
import { perchMachineServices } from './defs/services';
import {
  PerchEnergyContext,
  PerchEnergyEvent,
  PerchEnergyParams,
  PerchEnergyServices,
} from './defs/types';

export const perchEnergyMachine = (params: PerchEnergyParams) =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as PerchEnergyContext,
        events: {} as PerchEnergyEvent,
        services: {} as PerchEnergyServices,
      },
      context: {
        queryParams: {},
      },
      id: 'perchEnergy',
      initial: 'start',
      states: {
        start: {
          entry: ['setFunnelId'],
          always: { target: 'utility' },
        },
        utility: {
          invoke: {
            id: 'utilityMachine',
            src: 'utilityMachine',
            data: utilityContext(funnelContext => utilityContextOverrides(funnelContext, params)),
            onDone: {
              target: 'fetchFeatureFlags',
              actions: [
                'setUtilitySelection',
                'addUtilitySelectionToStore',
                'setFunnelVnmProjectId',
              ],
            },
          },
        },
        fetchFeatureFlags: {
          invoke: {
            id: 'launchDarklyIdentify',
            src: 'launchDarklyIdentify',
            onDone: [
              {
                cond: 'isSignupAvailable',
                target: 'user',
              },

              { target: 'csUnavailable' },
            ],
            onError: { target: 'user', actions: 'trackFetchFeatureFlagsError' },
          },
        },
        user: {
          invoke: {
            id: 'userMachine',
            src: 'userMachine',
            data: userContext(funnelContext => userContextOverrides(funnelContext, params)),
            onDone: [
              {
                cond: 'requiresAffirmativeConsent',
                target: 'updateRequireAffirmativeConsent',
                actions: 'setFunnelPacket',
              },
              {
                target: 'getEnrollmentOptions',
                actions: 'setFunnelPacket',
              },
            ],
          },
          on: {
            CHANGE_UTILITY: { target: 'utility', actions: 'clearUtilitySelection' },
          },
        },
        getEnrollmentOptions: {
          entry: ['setEnrollmentOptions'],
          always: [{ target: 'enrollment' }],
        },
        enrollment: {
          id: 'enrollment',
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext =>
              enrollmentContextOverrides(funnelContext, params),
            ),
            onDone: [
              {
                target: 'upload',
                actions: [
                  'setEnrollmentSelection',
                  'setNyLmiZoneOptions',
                  'setIsInLmiArea',
                  'setUtilityConnectStatus',
                ],
              },
            ],
          },
        },
        updateRequireAffirmativeConsent: {
          invoke: {
            id: 'updateRequireAffirmativeConsent',
            src: 'updateRequireAffirmativeConsent',
            onDone: {
              target: 'getEnrollmentOptions',
            },
            onError: 'unexpected',
          },
        },
        upload: {
          id: 'upload',
          invoke: {
            id: 'uploadMachine',
            src: 'uploadMachine',
            data: uploadContext(funnelContext => uploadContextOverrides(funnelContext, params)),
            onDone: {
              target: 'completed',
            },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
        csUnavailable: {
          id: 'csUnavailable',
          onEntry: ['trackFunnelCompleted', 'trackCSUnavailable'],
        },
        completed: {
          type: 'final',
        },
      },
    },
    {
      /**
       * There is a problem with importing actions like this
       * from a different file. Apparently, the Xstate's typegen
       * statically evaluates the machine.ts to infer types and
       * generate the typegen. Importing it like this means, Xstate
       * cannot "see" the action's implementation. But writing
       * actions inline is not ideal. We are looking to make these actions
       * simplified and until that, actions are not strongly typed.
       *
       */
      actions: perchMachineActions,
      guards: perchMachineGuards,
      services: perchMachineServices(params),
    },
  );
